import {
  DestroyRef,
  Directive,
  ElementRef,
  ViewContainerRef,
  booleanAttribute,
  computed,
  inject,
  input,
} from '@angular/core';

import { AssistantUiService } from '@core/assistant/assistant-ui.service';
import { attachProposalUi } from './assistant-proposal-ui';

/**
 * Якорь для поля, у которого НЕТ формы.
 *
 * `snAssistantEditable` цепляется к `ngModel` и покрывает обычные поля. Но
 * часть состояния редактируется не контролом, а своим виджетом — выбор
 * модели открывает диалог и отдаёт объект с версией и параметрами
 * сэмплирования. Такое поле компонент регистрирует сам (`registerField`),
 * а сюда приходит только за видимой частью: подсветить нужный элемент и
 * показать под ним карточку предложения.
 *
 * ```html
 * <app-model-selector-button snAssistantAnchor="agent.llmVersion" ...>
 * ```
 */
@Directive({
  selector: '[snAssistantAnchor]',
  standalone: true,
  host: {
    '[class.sn-anchor-pending]': 'pending()',
    '[class.sn-anchor-flash]': 'flashing()',
  },
})
export class AssistantAnchorDirective {
  private readonly ui = inject(AssistantUiService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Локальное имя поля — префикс поверхности добавится сам. Пустая строка
   * выключает якорь: в каскаде моделей полем агента считается только
   * первая строка, остальные он не трогает.
   */
  readonly localId = input.required<string>({ alias: 'snAssistantAnchor' });
  /**
   * Карточка предложения прямо у элемента. Для полей — да, там она и
   * показывает дифф. Для кнопки в полосе действий — нет: ляжет поверх
   * самой кнопки, на которую указывает. Разбираемся по тому, поле это
   * или действие (см. `cardVisible`).
   */
  readonly inlineCard = input(true, { alias: 'snAssistantAnchorCard', transform: booleanAttribute });

  private readonly targetId = computed(() => {
    const local = this.localId();
    if (!local) return '';
    const surface = this.ui.surfaceFor(this.host.nativeElement);
    return surface ? surface.fieldId(local) : local;
  });

  protected readonly pending;
  protected readonly flashing;

  constructor() {
    const ui = attachProposalUi({
      ui: this.ui,
      vcr: this.vcr,
      destroyRef: this.destroyRef,
      targetId: this.targetId,
      // У действия карточки нет: что агент предлагает нажать, рассказывает
      // панель, а у кнопки остаётся обводка. То же правило, что у
      // snAssistantAction.
      inlineCard: computed(() => this.inlineCard() && !this.ui.isAction(this.targetId())),
      host: computed(() => this.host.nativeElement),
    });
    this.pending = ui.pending;
    this.flashing = ui.flashing;

    // Регистрацию поля делает компонент; нам нужен только его элемент —
    // по нему сервис скроллит к полю и подсвечивает его.
    const el = this.host.nativeElement;
    let attached = '';
    const sync = () => {
      const id = this.targetId();
      if (id === attached) return;
      if (attached) this.ui.detachElement(attached, el);
      if (id) this.ui.attachElement(id, el);
      attached = id;
    };
    queueMicrotask(sync);
    this.destroyRef.onDestroy(() => {
      if (attached) this.ui.detachElement(attached, el);
    });
  }
}
